import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    // Check for superadmin token first (for superadmin routes)
    if (config.url?.startsWith('/superadmin')) {
        const superadminToken = localStorage.getItem('superadmin_token');
        if (superadminToken) {
            config.headers.Authorization = `Bearer ${superadminToken}`;
            return config;
        }
    }
    
    // Otherwise use regular token
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const pathname = window.location.pathname;
            const isAuthPage = pathname === '/login' || pathname === '/register' || pathname.startsWith('/superadmin');
            if (!isAuthPage) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Helper function to get error message from API response
export const getErrorMessage = (error, defaultMsg = 'An error occurred') => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') {
        return detail;
    } else if (Array.isArray(detail)) {
        return detail.map(d => typeof d === 'string' ? d : (d.msg || JSON.stringify(d))).join(', ');
    } else if (detail && typeof detail === 'object') {
        return detail.msg || JSON.stringify(detail);
    }
    return error.message || defaultMsg;
};

// Auth APIs
export const authAPI = {
    register: (data, invitationToken = null, companyInvitation = null) => {
        const params = new URLSearchParams();
        if (invitationToken) params.append('invitation_token', invitationToken);
        if (companyInvitation) params.append('company_invitation', companyInvitation);
        const qs = params.toString();
        const url = qs ? `/auth/register?${qs}` : '/auth/register';
        return api.post(url, data);
    },
    login: (data) => api.post('/auth/login', data),
    googleAuth: (data) => api.post('/auth/google', data),
    logout: () => api.post('/auth/logout'),
    getMe: () => api.get('/auth/me'),
    updateMe: (data) => api.put('/auth/me', data),
    verifyInvitation: (token) => api.get(`/invitations/verify/${token}`),
};

// Workspace APIs
export const workspaceAPI = {
    create: (data) => api.post('/workspaces', data),
    getAll: () => api.get('/workspaces'),
    get: (id) => api.get(`/workspaces/${id}`),
    update: (id, data) => api.put(`/workspaces/${id}`, data),
    delete: (id) => api.delete(`/workspaces/${id}`),
    getMembers: (id) => api.get(`/workspaces/${id}/members`),
    addMember: (id, data) => api.post(`/workspaces/${id}/members`, data),
    updateMember: (workspaceId, userId, data) => api.put(`/workspaces/${workspaceId}/members/${userId}`, data),
    removeMember: (workspaceId, userId) => api.delete(`/workspaces/${workspaceId}/members/${userId}`),
    getStorageStats: (id) => api.get(`/workspaces/${id}/storage`),
    getStats: (id, spaceIds = null) => {
        const params = spaceIds ? { space_ids: spaceIds.join(',') } : {};
        return api.get(`/workspaces/${id}/stats`, { params });
    },
    getMyTaskStats: (id, spaceIds = null) => {
        const params = spaceIds ? { space_ids: spaceIds.join(',') } : {};
        return api.get(`/workspaces/${id}/stats/my-task`, { params });
    },
    getReviewByMeStats: (id, spaceIds = null) => {
        const params = spaceIds ? { space_ids: spaceIds.join(',') } : {};
        return api.get(`/workspaces/${id}/stats/review-by-me`, { params });
    },
    getAssignedByMeStats: (id, spaceIds = null) => {
        const params = spaceIds ? { space_ids: spaceIds.join(',') } : {};
        return api.get(`/workspaces/${id}/stats/assigned-by-me`, { params });
    },
    getInvitations: (id) => api.get(`/workspaces/${id}/invitations`),
    resendInvitations: (workspaceId, invitationId) => api.post(`/workspaces/${workspaceId}/invitations/${invitationId}/resend`),
    cancelInvitation: (workspaceId, invitationId) => api.delete(`/workspaces/${workspaceId}/invitations/${invitationId}`),
    // Workspace Settings (Task Categories + Meeting)
    getSettings: (id) => api.get(`/workspaces/${id}/settings`),
    updateSettings: (id, data) => api.put(`/workspaces/${id}/settings`, data),
    getNotStartedTasks: (workspaceId, userId) => api.get(`/workspaces/${workspaceId}/tasks?assignee_id=${userId}&group=Not Started`),
    getStatTimeline: (workspaceId, year, month, spaceId = null) => {
        const params = { year, month };
        if (spaceId) params.space_id = spaceId;
        return api.get(`/workspaces/${workspaceId}/stat-timeline`, { params });
    },
    getStatTimelineAllTime: (workspaceId, spaceId = null) => {
        const params = {};
        if (spaceId) params.space_id = spaceId;
        return api.get(`/workspaces/${workspaceId}/stat-timeline/alltime`, { params });
    },
};

// Meeting APIs
export const meetingAPI = {
    create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/meetings`, data),
    getAll: (workspaceId, startDate, endDate) => api.get(`/workspaces/${workspaceId}/meetings`, { params: { start_date: startDate, end_date: endDate } }),
    get: (workspaceId, meetingId) => api.get(`/workspaces/${workspaceId}/meetings/${meetingId}`),
    delete: (workspaceId, meetingId) => api.delete(`/workspaces/${workspaceId}/meetings/${meetingId}`),
};

// Space APIs
export const spaceAPI = {
    create: (data) => api.post('/spaces', data),
    getByWorkspace: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/spaces`, { params }),
    get: (id) => api.get(`/spaces/${id}`),
    update: (id, data) => api.put(`/spaces/${id}`, data),
    delete: (id) => api.delete(`/spaces/${id}`),
    archive: (id) => api.patch(`/spaces/${id}/archive`),
    unarchive: (id) => api.patch(`/spaces/${id}/unarchive`),
    // Space Settings
    getSpaceSettings: (id) => api.get(`/spaces/${id}/settings`),
    updateSpaceSettings: (id, data) => api.put(`/spaces/${id}/settings`, data),
    addMemberToSpace: (spaceId, userId) => api.post(`/spaces/${spaceId}/members/${userId}`),
    removeMemberFromSpace: (spaceId, userId) => api.delete(`/spaces/${spaceId}/members/${userId}`),
    addSpaceAdmin: (spaceId, userId) => api.post(`/spaces/${spaceId}/admins/${userId}`),
    removeSpaceAdmin: (spaceId, userId) => api.delete(`/spaces/${spaceId}/admins/${userId}`),
    getAssignedMembers: (spaceId) => api.get(`/spaces/${spaceId}/assigned-members`),
};

// Folder APIs
export const folderAPI = {
    create: (data) => api.post('/folders', data),
    getBySpace: (spaceId) => api.get(`/spaces/${spaceId}/folders`),
    update: (id, data) => api.put(`/folders/${id}`, data),
    delete: (id) => api.delete(`/folders/${id}`),
};

// List APIs
export const listAPI = {
    create: (data) => api.post('/lists', data),
    getBySpace: (spaceId) => api.get(`/spaces/${spaceId}/lists`),
    getByFolder: (folderId) => api.get(`/folders/${folderId}/lists`),
    get: (id) => api.get(`/lists/${id}`),
    update: (id, data) => api.put(`/lists/${id}`, data),
    delete: (id) => api.delete(`/lists/${id}`),
};

// Task APIs
export const taskAPI = {
    create: (data) => api.post('/tasks', data),
    getLogs: (taskId) => api.get(`/tasks/${taskId}/audit-logs`),
    getByList: (listId) => api.get(`/lists/${listId}/tasks`),
    getBySpace: (spaceId) => api.get(`/spaces/${spaceId}/tasks`),
    getByWorkspace: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/tasks`, { params }),
    getReviewByMe: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/tasks/review-by-me`, { params }),
    getAssignedByMe: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/tasks/assigned-by-me`, { params }),
    getDeletedTaskBySpace: (spaceId) => api.get(`/spaces/${spaceId}/deleted-tasks`),
    getDeletedTask: (id) => api.get(`/deleted-tasks/${id}`),
    getDeletedSubtasks: (id) => api.get(`/deleted-tasks/${id}/subtasks`),
    get: (id) => api.get(`/tasks/${id}`),
    getSubtasks: (id) => api.get(`/tasks/${id}/subtasks`),
    update: (id, data) => api.put(`/tasks/${id}`, data),
    quickChangeStatus: (id) => api.put(`/tasks/${id}/next-status`),
    duplicate: (id) => api.post(`/tasks/${id}/duplicate`),
    delete: (id) => api.delete(`/tasks/${id}`),
    checkOverload: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/tasks/overload-check`, { params }),
};

// Daily Standup APIs
export const dailyStandupAPI = {
    getAll: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/daily-standup`, { params }),
    getMySummary: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/daily-standup/my-summary`, { params }),
    dismiss: (workspaceId, date, continueOngoing = false) => api.post(`/workspaces/${workspaceId}/daily-standup/dismiss`, { date, continue_ongoing: continueOngoing }),
    getPopupStatus: (workspaceId) => api.get(`/workspaces/${workspaceId}/daily-standup/popup-status`),
    sendReminders: (workspaceId, message) => api.post(`/workspaces/${workspaceId}/daily-standup/send-reminders`, { message }),
    updatePlan: (workspaceId, date, plannedTaskIds) =>
        api.post(`/workspaces/${workspaceId}/daily-standup/update-plan`, { date, planned_task_ids: plannedTaskIds }),
};

// Custom Field APIs
export const customFieldAPI = {
    create: (data) => api.post('/custom-fields', data),
    getBySpace: (spaceId) => api.get(`/spaces/${spaceId}/custom-fields`),
    getByList: (listId) => api.get(`/lists/${listId}/custom-fields`),
    update: (id, data) => api.put(`/custom-fields/${id}`, data),
    delete: (id) => api.delete(`/custom-fields/${id}`),
};

// Time Log APIs
export const timeLogAPI = {
    create: (data) => api.post('/time-logs', data),
    getByTask: (taskId) => api.get(`/tasks/${taskId}/time-logs`),
    getByWorkspace: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/time-logs`, { params }),
    update: (id, data) => api.put(`/time-logs/${id}`, data),
    delete: (id) => api.delete(`/time-logs/${id}`),
};

// Timer APIs
export const timerAPI = {
    start: (taskId) => api.post(`/timers/start?task_id=${taskId}`),
    stop: () => api.post('/timers/stop'),
    getActive: () => api.get('/timers/active'),
};

// Goal APIs
export const goalAPI = {
    create: (data) => api.post('/goals', data),
    getByWorkspace: (workspaceId) => api.get(`/workspaces/${workspaceId}/goals`),
    get: (id) => api.get(`/goals/${id}`),
    update: (id, data) => api.put(`/goals/${id}`, data),
    delete: (id) => api.delete(`/goals/${id}`),
};

// Webhook APIs
export const webhookAPI = {
    create: (data) => api.post('/webhooks', data),
    getByWorkspace: (workspaceId) => api.get(`/workspaces/${workspaceId}/webhooks`),
    update: (id, data) => api.put(`/webhooks/${id}`, data),
    delete: (id) => api.delete(`/webhooks/${id}`),
};

// Audit Log APIs
export const auditLogAPI = {
    getByWorkspace: (workspaceId, params) => api.get(`/workspaces/${workspaceId}/audit-logs`, { params }),
};

// Comment APIs
export const commentAPI = {
    create: (data) => api.post('/comments', data),
    getByTask: (taskId) => api.get(`/tasks/${taskId}/comments`),
    update: (id, data) => api.put(`/comments/${id}`, data),
    delete: (id) => api.delete(`/comments/${id}`),
};

// Calendar APIs
export const calendarAPI = {
    getEvents: (workspaceId, startDate, endDate) => 
        api.get(`/workspaces/${workspaceId}/calendar`, { params: { start_date: startDate, end_date: endDate } }),
};

// Timeline APIs
export const timelineAPI = {
    getSpaceTimeline: (spaceId, startDate, endDate) => 
        api.get(`/spaces/${spaceId}/timeline`, { params: { start_date: startDate, end_date: endDate } }),
};

// Client Dashboard APIs
export const clientDashboardAPI = {
    get: (spaceId) => api.get(`/spaces/${spaceId}/client-dashboard`),
    getStatTimeline: (spaceId, year, month) =>
        api.get(`/spaces/${spaceId}/stat-timeline`, { params: { year, month } }),
};

// File Upload APIs
export const fileAPI = {
    upload: (file, taskId = null, spaceId = null, folderId = null) => {
        const formData = new FormData();
        formData.append('file', file);
        if (taskId) formData.append('task_id', taskId);
        if (spaceId) formData.append('space_id', spaceId);
        if (folderId) formData.append('folder_id', folderId);
        return api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getUrl: (filename) => `${API_BASE}/files/${filename}`,
    getBySpace: (spaceId) => api.get(`/spaces/${spaceId}/files`),
    delete: (fileId) => api.delete(`/files/${fileId}`),
    moveToFolder: (fileId, folderId) => api.patch(`/file-folders/files/${fileId}/move`, { folder_id: folderId }),
};

// Lead APIs
export const leadAPI = {
    create: (data) => api.post('/leads', data),
    getByWorkspace: (workspaceId) => api.get(`/workspaces/${workspaceId}/leads`),
    get: (id) => api.get(`/leads/${id}`),
    update: (id, data) => api.put(`/leads/${id}`, data),
    delete: (id) => api.delete(`/leads/${id}`),
};

// PM Client APIs
export const pmClientAPI = {
    create: (data) => api.post('/pm-clients', data),
    getByWorkspace: (workspaceId) => api.get(`/workspaces/${workspaceId}/pm-clients`),
    get: (id) => api.get(`/pm-clients/${id}`),
    update: (id, data) => api.put(`/pm-clients/${id}`, data),
    delete: (id) => api.delete(`/pm-clients/${id}`),
};

// Project APIs
export const projectAPI = {
    create: (data) => api.post('/projects', data),
    getByWorkspace: (workspaceId) => api.get(`/workspaces/${workspaceId}/projects`),
    get: (id) => api.get(`/projects/${id}`),
    update: (id, data) => api.put(`/projects/${id}`, data),
    delete: (id) => api.delete(`/projects/${id}`),
};

// ===== Discussion/Chat APIs =====

// Channel APIs
export const channelAPI = {
    create: (workspaceId, data) => api.post(`/workspaces/${workspaceId}/channels`, data),
    getByWorkspace: (workspaceId) => api.get(`/workspaces/${workspaceId}/channels`),
    get: (channelId) => api.get(`/channels/${channelId}`),
    update: (channelId, data) => api.put(`/channels/${channelId}`, data),
    delete: (channelId) => api.delete(`/channels/${channelId}`),
    getMembers: (channelId) => api.get(`/channels/${channelId}/members`),
    addMembers: (channelId, userIds) => api.post(`/channels/${channelId}/members`, { user_ids: userIds }),
    removeMembers: (channelId, userIds) => api.delete(`/channels/${channelId}/members`, { data: { user_ids: userIds } }),
    join: (channelId) => api.post(`/channels/${channelId}/join`),
    leave: (channelId) => api.post(`/channels/${channelId}/leave`),
    // Mute
    mute: (channelId) => api.post(`/channels/${channelId}/mute`),
    unmute: (channelId) => api.delete(`/channels/${channelId}/mute`),
    // Settings
    getSettings: (channelId) => api.get(`/channels/${channelId}/settings`),
    updateSettings: (channelId, data) => api.put(`/channels/${channelId}/settings`, data),
    // Read status
    markAsRead: (channelId, upToMessageId = null) => 
        api.post(`/channels/${channelId}/read`, null, { params: { up_to_message_id: upToMessageId } }),
};

// Message APIs
export const messageAPI = {
    getByChannel: (channelId, params = {}) => api.get(`/channels/${channelId}/messages`, { params }),
    create: (channelId, data) => api.post(`/channels/${channelId}/messages`, data),
    get: (messageId) => api.get(`/messages/${messageId}`),
    getReplies: (messageId, params = {}) => api.get(`/messages/${messageId}/replies`, { params }),
    update: (messageId, data) => api.put(`/messages/${messageId}`, data),
    delete: (messageId) => api.delete(`/messages/${messageId}`),
    // Reactions
    addReaction: (messageId, emoji) => api.post(`/messages/${messageId}/reactions`, { emoji }),
    removeReaction: (messageId, emoji) => api.delete(`/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`),
    // Pin/Unpin
    togglePin: (messageId) => api.post(`/messages/${messageId}/pin`),
    getPinnedMessages: (channelId) => api.get(`/channels/${channelId}/pinned-messages`),
    // Bookmark
    toggleBookmark: (messageId) => api.post(`/messages/${messageId}/bookmark`),
    getBookmarks: () => api.get('/bookmarks'),
    // Edit
    editMessage: (messageId, content) => api.put(`/messages/${messageId}`, { content }),
    // Delete
    deleteMessage: (messageId) => api.delete(`/messages/${messageId}`),
    // Search
    search: (channelId, query) => api.get(`/channels/${channelId}/messages/search`, { params: { q: query } }),
    // Read
    markAsRead: (messageId) => api.post(`/messages/${messageId}/read`),
};

// Chat File Upload APIs
export const chatFileAPI = {
    upload: (file, channelId = null, conversationId = null) => {
        const formData = new FormData();
        formData.append('file', file);
        if (channelId) formData.append('channel_id', channelId);
        if (conversationId) formData.append('conversation_id', conversationId);
        return api.post('/chat/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getChannelFiles: (channelId) => api.get(`/chat/files/${channelId}`),
    deleteFile: (fileId) => api.delete(`/chat/files/${fileId}`),
};

// Direct Message / Conversation APIs
export const conversationAPI = {
    create: (workspaceId, participantIds) => api.post(`/workspaces/${workspaceId}/conversations`, { 
        participant_ids: participantIds,
        workspace_id: workspaceId
    }),
    getByWorkspace: (workspaceId) => api.get(`/workspaces/${workspaceId}/conversations`),
    getMessages: (conversationId, params = {}) => api.get(`/conversations/${conversationId}/messages`, { params }),
    sendMessage: (conversationId, data) => api.post(`/conversations/${conversationId}/messages`, data),
    deleteConversation: (conversationId) => api.delete(`/conversations/${conversationId}`),
};

// Presence APIs
export const presenceAPI = {
    getWorkspacePresence: (workspaceId) => api.get(`/workspaces/${workspaceId}/presence`),
    getOnlineUsers: (workspaceId) => api.get(`/workspaces/${workspaceId}/presence/online`),
    updateMyPresence: (workspaceId, status, customStatus = null) => 
        api.put(`/workspaces/${workspaceId}/presence/me`, null, { 
            params: { status, custom_status: customStatus } 
        }),
};

// Helper function to get WebSocket URL for chat
export const getChatWebSocketUrl = (channelId, userId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    // Extract host from backend URL
    const urlObj = new URL(backendUrl);
    const token = localStorage.getItem('token');
    const tokenParam = token ? `?token=${token}` : '';
    return `${wsProtocol}//${urlObj.host}/ws/chat/${channelId}/${userId}${tokenParam}`;
};

export default api;


// Bug Analysis APIs
export const bugAPI = {
    analyzeSimilarity: (data) => api.post('/bugs/analyze-similarity', data),
    getKnowledgeBase: (workspaceId, limit = 50) => api.get(`/bugs/knowledge-base/${workspaceId}`, { params: { limit } }),
};


// Company APIs
export const companyAPI = {
    // Members
    getMembers: (companyId, params) => api.get(`/companies/${companyId}/members`, { params }),

    // Workspace admins (multi)
    listWorkspaceAdmins: (companyId, workspaceId) =>
        api.get(`/companies/${companyId}/workspaces/${workspaceId}/admins`),
    setWorkspaceAdmin: (companyId, workspaceId, adminUserId) =>
        api.put(`/companies/${companyId}/workspaces/${workspaceId}/set-admin`, { admin_user_id: adminUserId }),
    removeWorkspaceAdmin: (companyId, workspaceId, userId) =>
        api.delete(`/companies/${companyId}/workspaces/${workspaceId}/admins/${userId}`),
    
    // Zoom Settings
    saveZoomSettings: (companyId, data) => api.post(`/companies/${companyId}/zoom-settings`, data),
    getZoomSettings: (companyId) => api.get(`/companies/${companyId}/zoom-settings`),
    deleteZoomSettings: (companyId) => api.delete(`/companies/${companyId}/zoom-settings`),
    
    // Zimbra Settings
    saveZimbraConfig: (companyId, data) => api.post(`/companies/${companyId}/zimbra-config`, data),
    getZimbraConfig: (companyId) => api.get(`/companies/${companyId}/zimbra-config`),
    deleteZimbraConfig: (companyId) => api.delete(`/companies/${companyId}/zimbra-config`),
    provisionZimbraUser: (companyId, userId) => api.post(`/companies/${companyId}/zimbra-provision-user/${userId}`),
    syncAllZimbraUsers: (companyId) => api.post(`/companies/${companyId}/zimbra-sync-all`),
    
    // API Keys
    createApiKey: (companyId, data) => api.post(`/companies/${companyId}/api-keys`, data),
    listApiKeys: (companyId) => api.get(`/companies/${companyId}/api-keys`),
    getAvailablePermissions: (companyId) => api.get(`/companies/${companyId}/api-keys/permissions`),
    updateApiKey: (companyId, keyId, data) => api.put(`/companies/${companyId}/api-keys/${keyId}`, data),
    deleteApiKey: (companyId, keyId) => api.delete(`/companies/${companyId}/api-keys/${keyId}`),
};

// Mail APIs
export const mailAPI = {
    getInbox: (limit = 50) => api.get('/mail/inbox', { params: { limit } }),
    getEmail: (emailId) => api.get(`/mail/email/${emailId}`),
    sendEmail: (data) => api.post('/mail/send', data),
    getAccount: () => api.get('/mail/account'),
    // DB-backed endpoints (Gmail-like UI)
    syncEmails: (limit = 100) => api.post('/mail/sync', null, { params: { limit } }),
    getEmails: (folder = 'inbox', { search = '', skip = 0, limit = 50 } = {}) =>
        api.get('/mail/emails', { params: { folder, search: search || undefined, skip, limit } }),
    toggleStar: (emailId, starred) => api.post(`/mail/star/${emailId}`, null, { params: { starred } }),
    toggleImportant: (emailId, important) => api.post(`/mail/important/${emailId}`, null, { params: { important } }),
    trashEmail: (emailId) => api.delete(`/mail/${emailId}`),
    markAsRead: (emailId, read = true) => api.post(`/mail/read/${emailId}`, null, { params: { read } }),
    // Drafts
    saveDraft: (payload) => api.post('/mail/drafts', payload),
    getDraft: (draftId) => api.get(`/mail/drafts/${draftId}`),
    deleteDraft: (draftId) => api.delete(`/mail/drafts/${draftId}`),
    // Zimbra Account Management
    resyncZimbraAccount: (accountId) => api.post(`/mail/zimbra/accounts/${accountId}/resync`),
    testZimbraSMTP: (accountId) => api.post(`/mail/zimbra/accounts/${accountId}/test-smtp`),
    syncZimbraPassword: (accountId) => api.post(`/mail/zimbra/accounts/${accountId}/sync-password`),
    revealZimbraPassword: (accountId, password) => api.post(`/mail/zimbra/accounts/${accountId}/reveal-password`, { password }),
};

// Helper function to get WebSocket URL for mail notifications
// Uses `/api/ws/mail/...` because the platform ingress only proxies `/api/*`
// to the backend (supports Upgrade: websocket).
export const notificationAPI = {
    getList: (workspaceId, skip = 0, limit = 20) =>
        api.get('/notifications', { params: { workspace_id: workspaceId, skip, limit } }),
    getUnreadCount: (workspaceId) =>
        api.get('/notifications/unread-count', { params: { workspace_id: workspaceId } }),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: (workspaceId) => api.put('/notifications/read-all', { workspace_id: workspaceId }),
    deleteOne: (id) => api.delete(`/notifications/${id}`),
};

export const getMailWebSocketUrl = (userId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const urlObj = new URL(backendUrl);
    const token = localStorage.getItem('token');
    const tokenParam = token ? `?token=${token}` : '';
    return `${wsProtocol}//${urlObj.host}/api/ws/mail/${userId}${tokenParam}`;
};